import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { dispatchAuthLogout } from './authHelper';
import { logoutApi } from '../api/api';

// ─────────────────────────────────────────
// AUTH CONTEXT
// ─────────────────────────────────────────
// Normal sessions (platform admin, club admin, members):
//   token → httpOnly cookie 'st_token' (set/cleared by server, never readable by JS)
//   user  → localStorage['user']  (non-sensitive profile data only)
//
// Impersonation tabs:
//   token → sessionStorage['__active_impersonate_token']  (set by Impersonate.jsx)
//   user  → sessionStorage['__active_impersonate_user']   (set by loginImpersonate)
//
// Demo sessions:
//   token → httpOnly cookie (same as normal)
//   flag  → localStorage['isDemo'] = 'true'  (cleared on logout)
//
// The two sets of keys NEVER overlap, so the platform admin tab's
// localStorage is completely untouched by impersonation tabs.
// ─────────────────────────────────────────

const IMP_TOKEN_KEY = '__active_impersonate_token';
const IMP_USER_KEY  = '__active_impersonate_user';

// Is this tab an impersonation tab?
const isImpersonateTab = () =>
  Boolean(window.sessionStorage.getItem(IMP_TOKEN_KEY));

const readInitialToken = () => {
  if (window.location.pathname !== '/impersonate') {
    window.sessionStorage.removeItem(IMP_TOKEN_KEY);
    window.sessionStorage.removeItem(IMP_USER_KEY);
  }
  // Impersonation tabs still use sessionStorage Bearer token
  if (isImpersonateTab()) {
    return window.sessionStorage.getItem(IMP_TOKEN_KEY);
  }
  // Normal sessions: token is in httpOnly cookie — JS never reads it.
  // Return a sentinel so the app knows the user is logged in (user object in localStorage).
  return window.localStorage.getItem('user') ? '__cookie__' : null;
};

const readInitialUser = () => {
  if (window.location.pathname !== '/impersonate') {
    window.sessionStorage.removeItem(IMP_TOKEN_KEY);
    window.sessionStorage.removeItem(IMP_USER_KEY);
  }
  if (isImpersonateTab()) {
    const raw = window.sessionStorage.getItem(IMP_USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }
  const raw = window.localStorage.getItem('user');
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

// ─────────────────────────────────────────

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(readInitialToken);
  const [user,  setUser]       = useState(readInitialUser);

  // Normal login — cookie is already set by server before this is called.
  // We only store the non-sensitive user profile in localStorage.
  const login = (newUser) => {
    setTokenState('__cookie__');
    setUser(newUser);
    window.localStorage.setItem('user', JSON.stringify(newUser));
    window.localStorage.removeItem('selected_club_id');
    window.localStorage.removeItem('selected_club_name');
    window.localStorage.removeItem('club');
    window.localStorage.removeItem('isDemo');  // ARCH-A5: clear demo flag on re-login
    setSelectedClubId(null);  // sync React state — clearing localStorage alone isn't enough
    // NOTE: do NOT write token to localStorage — cookie handles auth
  };

  // Impersonation login — writes ONLY to sessionStorage under dedicated keys.
  const loginImpersonate = (meData) => {
    const impToken = window.sessionStorage.getItem(IMP_TOKEN_KEY);
    const impUser  = { ...meData, member_id: meData.member_profile?.member_id ?? null };
    setTokenState(impToken);
    setUser(impUser);
    window.sessionStorage.setItem(IMP_USER_KEY, JSON.stringify(impUser));
  };

  const logout = useCallback(async () => {
    // Server clears the httpOnly cookie in its response
    await logoutApi();
    setTokenState(null);
    setUser(null);
    if (isImpersonateTab()) {
      window.sessionStorage.removeItem(IMP_TOKEN_KEY);
      window.sessionStorage.removeItem(IMP_USER_KEY);
    } else {
      window.localStorage.removeItem('user');
      window.localStorage.removeItem('selected_club_id');
      window.localStorage.removeItem('isDemo');
      // No token to remove — it was never in localStorage
    }
    dispatchAuthLogout();
  }, []);

  useEffect(() => {
    const handleAuthLogout = () => {
      setTokenState(null);
      setUser(null);
    };
    window.addEventListener('authLogout', handleAuthLogout);
    return () => window.removeEventListener('authLogout', handleAuthLogout);
  }, []);

  const [selectedClubId, setSelectedClubId] = useState(
    () => Number(window.localStorage.getItem('selected_club_id')) || null
  );

  const selectClub = useCallback((clubId) => {
    if (clubId === null || clubId === undefined) {
      window.localStorage.removeItem('selected_club_id');
      setSelectedClubId(null);
    } else {
      const id = Number(clubId);
      window.localStorage.setItem('selected_club_id', String(id));
      setSelectedClubId(id);
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      const id = Number(window.localStorage.getItem('selected_club_id')) || null;
      setSelectedClubId(id);
    };
    window.addEventListener('platformClubSelected', sync);
    return () => window.removeEventListener('platformClubSelected', sync);
  }, []);

  const clubId = user?.is_platform_admin
    ? (selectedClubId || user?.club_id || null)
    : (user?.club_id ?? null);

  return (
    <AuthContext.Provider value={{ user, token, login, loginImpersonate, logout, clubId, selectClub, selectedClubId }}>
      {children}
    </AuthContext.Provider>
  );
};